import BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import ConversationNodeSortOrder from 'owa-service/lib/contract/ConversationNodeSortOrder';
import conversationRequestType from 'owa-service/lib/factory/conversationRequestType';
import ConversationRequestType from 'owa-service/lib/contract/ConversationRequestType';
import ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import folderId from 'owa-service/lib/factory/folderId';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getConversationItemsJsonRequest from 'owa-service/lib/factory/getConversationItemsJsonRequest';
import getConversationItemsOperation from 'owa-service/lib/operation/getConversationItemsOperation';
import getConversationItemsRequest from 'owa-service/lib/factory/getConversationItemsRequest';
import GetConversationItemsRequest from 'owa-service/lib/contract/GetConversationItemsRequest';
import GetConversationItemsResponseMessage from 'owa-service/lib/contract/GetConversationItemsResponseMessage';
import ItemId from 'owa-service/lib/contract/ItemId';
import itemId from 'owa-service/lib/factory/itemId';
import ItemResponseShape from 'owa-service/lib/contract/ItemResponseShape';
import { getBaseItemResponseShape } from './getItemResponseShape';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';

function configureResponseShape(): ItemResponseShape {
    const itemResponseShape = getBaseItemResponseShape();
    itemResponseShape.ExcludeBindForInlineAttachments = true;
    itemResponseShape.CalculateOnlyFirstBody = true;
    return itemResponseShape;
}

function convertConversationSortOrder(
    conversationSortOrder: ConversationSortOrder
): ConversationNodeSortOrder {
    // Note: only date order ascending is treated specially, all other values are treated as date order descending (the default).
    return conversationSortOrder === 'ChronologicalNewestOnBottom'
        ? 'DateOrderAscending'
        : 'DateOrderDescending';
}

function configureRequestBody(
    conversationToLoad: ConversationRequestType,
    responseShape: ItemResponseShape,
    conversationSortOrder: ConversationSortOrder,
    maxItemsToReturn: number
): GetConversationItemsRequest {
    return getConversationItemsRequest({
        Conversations: [conversationToLoad],
        ItemShape: responseShape,
        ShapeName: 'ItemPart',
        SortOrder: convertConversationSortOrder(conversationSortOrder),
        MaxItemsToReturn: maxItemsToReturn,
        FoldersToIgnore: getFoldersToIgnore(),
        ReturnSubmittedItems: true,
    });
}

function getFoldersToIgnore(): BaseFolderId[] {
    const outboxFolderId = folderNameToId('outbox');

    /**
     * This works for now since we don't support groups or public folders in mini-mail and
     * shouldReturnModernConversationItems is false for the scenarios, we should look for returnModernConversationItems
     * from mailboxInfo once we start supporting these scenarios. Check getConversationItemsService.ts implementation in react-mail.
     */
    return outboxFolderId
        ? [
              folderId({
                  Id: outboxFolderId,
              }),
          ]
        : [];
}

/**
 * Service wrapper for GCI call
 * @param conversationId
 * @param syncState
 * @param maxItemsToReturn
 * @param conversationSortOrder
 */
export default function getConversationItemsService(
    conversationId: ItemId,
    syncState: string,
    maxItemsToReturn: number,
    conversationSortOrder: ConversationSortOrder
): Promise<GetConversationItemsResponseMessage> {
    const responseShape = configureResponseShape();
    const conversation = conversationRequestType({ ConversationId: itemId(conversationId) });
    conversation.SyncState = syncState;

    const requestBody = configureRequestBody(
        conversation,
        responseShape,
        conversationSortOrder,
        maxItemsToReturn
    );
    const jsonRequestHeader = getJsonRequestHeader();
    jsonRequestHeader.RequestServerVersion = 'V2017_08_18';

    return getConversationItemsOperation(
        getConversationItemsJsonRequest({
            Header: jsonRequestHeader,
            Body: requestBody,
        })
    ).then(
        response => response.Body.ResponseMessages.Items[0] as GetConversationItemsResponseMessage
    );
}
